import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Input, Select, Table, Tooltip } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { ClipboardList, MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router';
import {
  POLICY_SORT_FIELDS,
  type Paginated,
  type PolicyDto,
  type PolicyListItemDto,
  type PolicySortField,
} from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { ChipList } from '@/components/ChipList';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { useAuth } from '@/hooks/useAuth';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import { can, mayAuthorPolicy, policyRefusal, actingOrgUnitTypeOf } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { archivedLabel } from '@/lib/labels';
import { queryKeys } from '@/lib/queryKeys';

/**
 * The policy list (FR-5.1.4).
 *
 * A policy is a COLUMN-SET TEMPLATE. The row shows what a CP nodal needs to choose one:
 * which version is live, whether there is an unpublished draft, whether it is the
 * default, and how many post columns it carries.
 *
 * `useServerTable`'s seventh consumer, again with no change to the hook.
 */
const PoliciesPage = () => {
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  /**
   * ⚠️ `modal` FROM `App.useApp()`, NOT THE STATIC `Modal.confirm`.
   *
   * Two reasons, and the second was measured. CLAUDE.md §8 already records that AntD
   * statics always use the ROOT theme and escape a `ThemeScope`. And the static does not
   * render AT ALL under jsdom — which is why no confirmation dialog in this repository
   * had a test before P2. The hook form renders, is themed, and is assertable.
   */
  const { message, modal } = App.useApp();
  const navigate = useNavigate();

  /*
   * ⚠️ [C5c §3] TWO GATES, BECAUSE THE SERVER HAS TWO. `New policy` is AUTHORING — the capability
   * and the commissionerate seat (`mayAuthorPolicy`), so an HQ nodal admin creates and a divisional
   * one does not. The row menu is CONFIGURING — archive, restore, the default — which is
   * `policy.configure`, SUPER_ADMIN. One flag for both is how widening `policy.write` would have
   * offered an HQ nodal three acts the route refuses.
   */
  const canAuthor = officer ? mayAuthorPolicy(officer.accessRole, actingOrgUnitTypeOf(officer)) : false;
  const canConfigure = officer ? can(officer.accessRole, 'policy.configure') : false;

  const table = useServerTable<PolicyListItemDto, PolicySortField>({
    sortFields: POLICY_SORT_FIELDS,
    defaultSortBy: 'name',
    defaultSortOrder: 'asc',
    filterKeys: ['show'],
    queryKey: (params) => queryKeys.policies.list(params),
    queryFn: (params, signal) =>
      api.get<Paginated<PolicyListItemDto>>('/policies', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          ...params.filters,
        },
        signal,
      }),
  });

  /*
   * ⚠️ `|| 'current'` — `useServerTable` STRIPS an empty filter value so it never reaches the
   * URL, which keeps a shared link clean; the page re-defaults on the way out, exactly as
   * `kind` does.
   */
  const show = table.params.filters.show || 'current';

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });

  const setDefault = useMutation({
    mutationFn: (id: string) => api.post<PolicyDto>(`/policies/${id}/default`),
    onSuccess: () => {
      void message.success('Default policy updated');
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.post<PolicyDto>(`/policies/${id}/archive`),
    onSuccess: () => {
      void message.success('Policy archived');
      invalidate();
    },
    // The API refuses to archive the default policy with a reason; surface it.
    onError: (error: ApiError) => void message.error(error.message),
  });

  /**
   * ⚠️ [LIFECYCLE] THIS PAGE HAD NO WAY BACK AT ALL, AND `POST /policies/:id/activate` HAD
   * EXISTED SINCE P2 WITH NO CALLER. Archiving a policy removed it from a list that could
   * only ever show live rows, so the row was unreachable and unrecoverable through the UI —
   * a worse dead end than the post-type one the brief reported, and nobody had noticed
   * because the endpoint's tests passed.
   */
  const restore = useMutation({
    mutationFn: (id: string) => api.post<PolicyDto>(`/policies/${id}/restore`),
    onSuccess: () => {
      void message.success('Policy restored');
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const confirmArchive = (policy: PolicyListItemDto) =>
    modal.confirm({
      title: `Archive ${policy.name}?`,
      content:
        'Archiving is used instead of deletion, because arrangements point at this policy’s versions. It stops ' +
        'appearing when a new arrangement is raised; everything already ordered is unaffected.',
      okText: 'Archive',
      okButtonProps: { danger: true },
      onOk: () => void archive.mutateAsync(policy.id),
    });

  /*
   * ⚠️ [C5c §3] RENDERED FOR EVERYBODY, DISABLED WITHOUT `policy.configure` — the design language's
   * "an in-page action is disabled, never hidden", which this menu has always kept for a reader and
   * its sibling master pages keep too. An HQ nodal admin authors and does not configure, so they
   * meet it exactly as a reader does.
   */
  const rowActions = (policy: PolicyListItemDto): MenuProps['items'] =>
    policy.archivedAt !== null
      ? [
          {
            key: 'restore',
            label: 'Restore',
            disabled: !canConfigure,
            onClick: () => void restore.mutateAsync(policy.id),
          },
        ]
      : [
          {
            key: 'default',
            label: 'Make this the default',
            disabled: !canConfigure || policy.isDefault || policy.archivedAt !== null,
            onClick: () => void setDefault.mutateAsync(policy.id),
          },
          {
            key: 'archive',
            label: 'Archive',
            danger: true,
            disabled: !canConfigure || policy.archivedAt !== null,
            onClick: () => void confirmArchive(policy),
          },
        ];

  const allColumns: TableProps<PolicyListItemDto>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      /**
       * A real LINK, because this one NAVIGATES — so ⌘-click opens a tab and the URL is
       * copyable, neither of which a button can do. It replaces the `Open` button that
       * used to sit in the actions column: two controls to the same place is one too
       * many, and the name is the one an operator reaches for.
       */
      /*
       * ⚠️ [D4.3] NO `secondary`, PER THE CLIENT. The description was never a COLUMN — it was
       * the second line of this cell — and it is what the column chips replace: an operator
       * scanning this list wants to know what a policy ASKS FOR, and a sentence somebody typed
       * once is not that. It is still on the wire and still on the policy screen.
       */
      render: (name: string, row) => <NameCell name={name} to={`/policies/${row.id}`} />,
    },
    {
      title: 'Version',
      key: 'currentVersion',
      width: 110,
      render: (_value, row) =>
        row.currentVersionNumber === null ? (
          <span className="text-subtle">—</span>
        ) : (
          <span className="tabular-nums">v{row.currentVersionNumber}</span>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_value, row) =>
        /*
         * ⚠️ [LIFECYCLE] AN ARCHIVED ROW SAYS WHAT IT WAS. `archivedLabel` composes the
         * preserved state rather than replacing it — a bare "Archived" throws away the only
         * fact that distinguishes a withdrawn published policy from a draft nobody issued.
         */
        row.archivedAt !== null ? (
          <StatusDot tone="neutral" label={archivedLabel(row.currentVersionNumber !== null ? 'published' : null)} />
        ) : // A published version and an open draft are not mutually exclusive: the draft is
        // the interesting fact, because it is what is about to change.
        row.draftVersionNumber !== null ? (
          <StatusDot tone="info" label={`Draft v${row.draftVersionNumber}`} />
        ) : row.currentVersionNumber !== null ? (
          <StatusDot tone="success" label="Published" />
        ) : (
          <StatusDot tone="neutral" label="No version" />
        ),
    },
    {
      title: 'Default',
      key: 'isDefault',
      width: 110,
      // A DOT on the one row, not a `StatusPill`. §4's dot-vs-pill rule keeps the pill
      // for a detail record's one primary state.
      render: (_value, row) =>
        row.isDefault ? <StatusDot tone="info" label="Default" /> : <span className="text-subtle">—</span>,
    },
    {
      /*
       * ⚠️ [D4.3] WHAT THE POLICY ASKS FOR, WITHOUT OPENING IT — and it is the COLUMN SET,
       * not a grid. A policy holds no numbers, so an actual table here would be an empty one.
       *
       * ⚠️ RETIRED COLUMNS ARE SHOWN, MUTED, NOT DROPPED. [FIX-1]'s rule is that a frozen
       * version is filtered where it is USED, never edited — and that the count DISCLOSES.
       * Hiding a retired column here would mean the list and the policy screen disagreed
       * about what a policy contains, which is the silent half of that same bug.
       *
       * ⚠️ AND NO COMMAND ROLES, per the client and per FR-5.2.6b. `policyListItemSchema`
       * does not carry them, so there is no argument through which one could arrive.
       */
      title: 'Columns',
      key: 'columns',
      width: 320,
      render: (_value, row) => (
        <ChipList
          chips={[
            ...row.postTypes.map((post) => ({
              key: `p:${post.id}`,
              label: post.shortName || post.name,
              title: post.archivedAt ? `${post.name} — retired` : post.name,
              muted: post.archivedAt !== null,
            })),
            ...row.vehicleTypes.map((vehicle) => ({
              key: `v:${vehicle.id}`,
              label: vehicle.code || vehicle.name,
              title: vehicle.archivedAt ? `${vehicle.name} — retired` : vehicle.name,
              muted: vehicle.archivedAt !== null,
            })),
          ]}
          overflowLabel={(hidden) => `Show ${String(hidden)} more columns on ${row.name}`}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      // [P4-UX] Pinned: an action a scroll can hide is an action that is not there.
      fixed: 'right' as const,
      align: 'right',
      width: 60,
      render: (_value, row) => (
        <div className="flex items-center justify-end gap-1">
          <Dropdown menu={{ items: rowActions(row) }} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" aria-label={`More actions for ${row.name}`}>
              <MoreHorizontal className="size-4" aria-hidden />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  /*
   * ⚠️ THE MENU GETS THE UNFILTERED ARRAY; THE TABLE GETS THE FILTERED ONE. That is the
   * circularity solved — by never letting the two be the same variable. Keys and locks are
   * DERIVED from the array above, so this page keeps no second list of columns.
   */
  const columnKeys = columnKeySet(allColumns ?? []);
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'policies' });
  const columns = (allColumns ?? []).filter((column) => columnVisibility.isVisible(String(column.key)));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={ClipboardList}
        title="Policies"
        subtitle={`${table.total} — each one a set of columns an arrangement is raised against`}
        actions={
          <Tooltip title={canAuthor ? undefined : policyRefusal(officer?.accessRole ?? 'NONE')}>
            <Button type="primary" disabled={!canAuthor} onClick={() => void navigate('/policies/new')}>
              New policy
            </Button>
          </Tooltip>
        }
        filters={
          <>
            {/*
              ⚠️ [LIFECYCLE] TWO AXES, TWO CONTROLS — never one mixed dropdown. `Show` says
              whether a row is in circulation; `Status` says where it is in its own life.
              Collapsing them would make "Archived" look like a peer of "Draft", which is the
              confusion the archive-is-an-axis design exists to prevent.

              ⚠️ A `Select`, NOT a `Segmented` — three values stop it being a switch, and here
              it would sit beside a Segmented and read as a second tab bar. No `allowClear`:
              "Current" is a value, not the absence of a filter.
            */}
            <FilterField label="Show" className="w-44">
              <Select
                className="w-full"
                value={show}
                onChange={(value: string) => table.setFilter('show', value === 'current' ? undefined : value)}
                options={[
                  { value: 'current', label: 'Current' },
                  { value: 'archived', label: 'Archived' },
                  { value: 'all', label: 'All' },
                ]}
                data-testid="show-filter"
              />
            </FilterField>
            <FilterField label="Search" className="w-64">
              <Input.Search
                allowClear
                placeholder="Name"
                value={table.searchInput}
                onChange={(event) => table.onSearchChange(event.target.value)}
                data-testid="policy-search"
              />
            </FilterField>
            {/* The Columns menu sits IN the filter row, not in `actions`: it is a view control,
                the same argument `pages/Arrangements` makes for its Segmented. */}
            <ColumnsMenu
              className="ml-auto"
              columns={allColumns ?? []}
              visibility={columnVisibility}
              locked={columnKeys.locked}
            />
          </>
        }
      />

      <Table<PolicyListItemDto>
        {...table.tableProps}
        columns={columns}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <EmptyState size="sm" title={table.searchInput ? 'No policies match this search.' : 'No policies yet.'} />
          ),
        }}
      />
    </div>
  );
};

export default PoliciesPage;
